import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights, hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import findKey from 'lodash/findKey';
import {
  PageTitle,
  PageContent,
  Tab,
  Flex,
  Link,
  Loader,
  Menu,
} from 'nallian-shared-ui/lib/components';
import { TABS, PRINCIPAL_TABS, PRINCIPAL_ACTIONS } from '../../organisations/constants';
import PrincipalIdentitiesTab from '../../organisations/components/principals/identities/PrincipalIdentities';
import PrincipalInformation from '../../organisations/components/principals/details/PrincipalInformation';
import PrincipalRoles from '../../organisations/components/principals/details/PrincipalRoles';
import PrincipalService from '../../organisations/services/principals/principalService';
import { isSystemOrganisation } from '../../../app/config/systemOrgs';
import EditPrincipal from '../../organisations/components/principals/EditPrincipal';

const TAB_INDEX_MAPPING = {
  [PRINCIPAL_TABS.DETAILS]: 0,
  [PRINCIPAL_TABS.ROLES]: 1,
  [PRINCIPAL_TABS.ACCOUNTS]: 2,
};

const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PrincipalDetail = ({ dispatchPrincipalGet, principalFetch }) => {
  const { t } = useTranslation();
  const { id, tab } = useParams();
  const navigate = useNavigate();

  const urlRoot = useMemo(() => `/principals/${id}`, [id]);

  useEffect(() => {
    if (id) dispatchPrincipalGet(id, t);
  }, [id, t, dispatchPrincipalGet]);

  const principal = principalFetch.value;

  const rights = useSelector(GetRights);
  const organisationId = principal?.organisation?.id;

  const isSystemOrg = useMemo(() => isSystemOrganisation(organisationId), [organisationId]);

  const forbidManageBecauseSystemOrg = useMemo(
    () =>
      isSystemOrganisation(organisationId) &&
      !hasRight(rights, 'ORGANISATIONS.MANAGE.ORGANISATION') &&
      hasSomeRights(rights, [
        'ORGANISATIONS.MANAGE.ALL',
        'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
      ]),
    [rights, organisationId]
  );

  const readOnly = useMemo(
    () => forbidManageBecauseSystemOrg || !hasSomeRights(rights, PRINCIPAL_RIGHTS),
    [rights, forbidManageBecauseSystemOrg]
  );

  return (
    <>
      <PageTitle flexed>
        <Flex alignItems="center">
          <Link to="/principals" marginright="1px">
            {t('allUsers')}
          </Link>
          &nbsp;/&nbsp;
          <Link to={`/principals/${id}/${TABS.DETAILS}`} marginright="1px">
            {principal?.fullName || '...'}
          </Link>
        </Flex>
      </PageTitle>
      <PageContent>
        <EditPrincipal
          forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
          urlRoot={urlRoot}
          organisationId={organisationId}
          isSystemOrg={isSystemOrg}
          open={tab === PRINCIPAL_ACTIONS.EDIT}
          id={id}
          returnRoute={urlRoot}
        />
        {principalFetch.pending ? (
          <Loader active inline />
        ) : (
          <Tab
            activeIndex={TAB_INDEX_MAPPING[tab]}
            onTabChange={(e, { activeIndex }) => {
              const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
              if (activeTab) {
                navigate(`${urlRoot}/${activeTab}`);
              }
            }}
            panes={[
              {
                menuItem: t('details'),
                render: () => (
                  <Tab.Pane>
                    <PrincipalInformation principal={principal} />
                  </Tab.Pane>
                ),
              },
              {
                menuItem: t('roles'),
                render: () => (
                  <Tab.Pane>
                    <PrincipalRoles principal={principal} />
                  </Tab.Pane>
                ),
              },
              {
                menuItem: t('accounts'),
                render: () => (
                  <Tab.Pane>
                    <PrincipalIdentitiesTab
                      isSystemOrg={false}
                      readOnly={readOnly}
                      identities={principal?.principalIdentities}
                      data={principal}
                      id={id}
                      isActive
                    />
                  </Tab.Pane>
                ),
              },
              {
                menuItem: (
                  <Menu.Menu position="right" key="menu">
                    <HasSomeRights rights={PRINCIPAL_RIGHTS}>
                      {!forbidManageBecauseSystemOrg ? (
                        <Menu.Item
                          icon="pencil"
                          content={t('edit')}
                          onClick={() => navigate(`${urlRoot}/edit`)}
                        />
                      ) : (
                        <div />
                      )}
                    </HasSomeRights>
                  </Menu.Menu>
                ),
              },
            ]}
          />
        )}
      </PageContent>
    </>
  );
};

PrincipalDetail.propTypes = {
  dispatchPrincipalGet: PropTypes.func.isRequired,
  principalFetch: PropTypes.object.isRequired,
};

export default PrincipalService(PrincipalDetail);
